import { Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { useAnalysisQuestionnaireEmptyPdf, useAnalysisQuestionnairePdf } from "Infrastructure/ApiZfp/AnalyzerApi";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export const QuestionnaireDocumentPage: FC = () => {
  const { t } = useResource();
  const { analysisId, versionId, type } = useParams() as { analysisId: string; versionId: string; type: string };
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);

  const isEmpty = type === "empty";
  const isFilled = type === "filled";
  const hasIds = !!analysisId && !!versionId;
  const queryOptions = { staleTime: Number.POSITIVE_INFINITY, retry: false };

  const emptyPdf = useAnalysisQuestionnaireEmptyPdf(analysisId, versionId, undefined, {
    query: { ...queryOptions, enabled: hasIds && isEmpty },
  });
  const filledPdf = useAnalysisQuestionnairePdf(analysisId, versionId, undefined, {
    query: { ...queryOptions, enabled: hasIds && isFilled },
  });
  const { data: pdf, isError: isLoadError } = isEmpty ? emptyPdf : filledPdf;
  const isError = isLoadError || (!isEmpty && !isFilled);

  useEffect(() => {
    if (!pdf) return;
    // Forcing the type makes the iframe render the PDF inline whatever content type the response came with.
    const objectUrl = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
    setPdfPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [pdf]);

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      height="100vh"
      width="100%"
      sx={(theme) => ({ background: theme.colors.backgroundSecondary })}
    >
      {isError ? (
        <Typography variant="body1">{t(Resources.AnalysisDetail.Pdf.LoadError)}</Typography>
      ) : pdfPreviewUrl ? (
        <iframe src={pdfPreviewUrl} style={{ border: 0, width: "100%", height: "100%" }} title="PDF Preview" />
      ) : (
        <Typography variant="body1">{t(Resources.AnalysisDetail.Pdf.Loading)}</Typography>
      )}
    </Flex>
  );
};
